import React, { useId } from "react";
import { getVariantLabel, type ProductVariant } from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./VariantDropdown.module.css";

/** Which surface the picker sits on — see the module CSS. */
export type VariantDropdownTone = "pdp" | "cart";

export interface VariantDropdownProps {
  variants: ProductVariant[];
  /** Chosen variant id, or null while the customer has not picked one. */
  selectedVariantId: string | null;
  onSelect: (variantId: string) => void;
  /**
   * Named so the control is unambiguous when several sit on one page — one per
   * bundled item. Read out after the visible label rather than replacing it.
   */
  productName?: string;
  tone?: VariantDropdownTone;
  /** Marks the control as an unresolved error state and wires up the message. */
  invalid?: boolean;
  /** Id of the message describing the error, for `aria-describedby`. */
  describedById?: string;
  /** True while a change is in flight; the control is held rather than removed. */
  busy?: boolean;
  /** Focused and scrolled to when the blocked checkout button points here. */
  selectRef?: React.Ref<HTMLSelectElement>;
}

/**
 * Version picker for a product sold in several variants (collar size, colour...).
 *
 * A real `<select>`, so keyboard handling, screen-reader semantics and the native
 * mobile picker all come for free; only its skin is ours. Used on the bundle
 * product page (one per bundled item) and on a cart line that still owes a
 * version.
 *
 * Nothing is preselected on purpose: the version has to be a deliberate choice,
 * so the empty option stays selectable-looking but disabled and the customer
 * cannot silently buy a default they never picked.
 */
export const VariantDropdown: React.FC<VariantDropdownProps> = ({
  variants,
  selectedVariantId,
  onSelect,
  productName,
  tone = "pdp",
  invalid = false,
  describedById,
  busy = false,
  selectRef,
}) => {
  const localizedTexts = useLocalizedTexts();
  const selectId = useId();

  if (!variants.length) return null;

  return (
    <div
      className={`${styles.row} ${tone === "cart" ? styles.cart : styles.pdp}`}
      data-component="variant-dropdown"
    >
      <label className={styles.label} htmlFor={selectId}>
        {t(localizedTexts, "Tracker.Products.ChooseVariant", "Choose version:")}
      </label>
      <select
        id={selectId}
        ref={selectRef}
        className={styles.select}
        value={selectedVariantId ?? ""}
        disabled={busy}
        aria-invalid={invalid || undefined}
        aria-describedby={describedById}
        aria-busy={busy || undefined}
        onChange={(event) => onSelect(event.target.value)}
      >
        <option value="" disabled>
          {t(localizedTexts, "Tracker.Products.SelectVariant", "Select...")}
        </option>
        {variants.map((variant) => (
          <option key={variant.id} value={variant.id}>
            {getVariantLabel(variant)}
          </option>
        ))}
      </select>
      {productName && <span className={styles.srOnly}>{productName}</span>}
    </div>
  );
};

export default VariantDropdown;
